import { useState, useCallback } from 'react';
import { useToast, useDisclosure } from '@chakra-ui/react';
import requestApi from 'utils/Request';
import { GROUPS } from 'utils/StaticApiEndpoints';

const useMerchantGroups = () => {
  const [merchantGroups, setMerchantGroups] = useState([]);
  // Multi-select, like Labels: several groups can be active together, each
  // union-ing its merchants into the pending selection.
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [isFetchingGroups, setIsFetchingGroups] = useState(false);
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);
  const [isUpdatingGroup, setIsUpdatingGroup] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [groupModalData, setGroupModalData] = useState({
    name: '',
    merchants: [],
    isEdit: false,
    editId: null,
    type: null,
  });

  const {
    isOpen: isGroupModalOpen,
    onOpen: openGroupModal,
    onClose: closeGroupModal,
  } = useDisclosure();

  const toast = useToast();

  const fetchGroups = useCallback(
    async (type = null) => {
      setIsFetchingGroups(true);
      try {
        const params = type ? { type } : {};
        const response = await requestApi.get(GROUPS, { params });
        setMerchantGroups(response.data || []);
      } catch (error) {
        toast({
          title: 'Error',
          description: 'Failed to fetch merchant groups',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setIsFetchingGroups(false);
      }
    },
    [toast],
  );

  const createGroup = async (groupData, type = null) => {
    setIsCreatingGroup(true);
    try {
      const response = await requestApi.post(GROUPS, groupData);
      await fetchGroups(type); // This won't show loader on create/update buttons
      return response;
    } catch (error) {
      toast({
        title: 'Error',
        description: error?.message || 'Failed to create merchant group',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      throw error;
    } finally {
      setIsCreatingGroup(false);
    }
  };

  const updateGroup = async (groupId, groupData, type = null) => {
    setIsUpdatingGroup(true);
    try {
      const response = await requestApi.put(`${GROUPS}/${groupId}`, groupData);
      await fetchGroups(type); // This won't show loader on create/update buttons
      return response;
    } catch (error) {
      toast({
        title: 'Error',
        description: error?.message || 'Failed to update merchant group',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      throw error;
    } finally {
      setIsUpdatingGroup(false);
    }
  };

  const handleGroupSelection = (
    group,
    pendingSelectedMerchant,
    setPendingSelectedMerchant,
  ) => {
    const isActive = selectedGroupIds.includes(group.id);
    const members = group.merchants || [];

    if (isActive) {
      const nextActiveIds = selectedGroupIds.filter((id) => id !== group.id);
      const stillCoveredIds = new Set(
        nextActiveIds.flatMap((id) => {
          const activeGroup = merchantGroups.find((g) => g.id === id);
          return (activeGroup?.merchants || []).map((m) => m.merchantId);
        }),
      );
      setSelectedGroupIds(nextActiveIds);
      setPendingSelectedMerchant(
        pendingSelectedMerchant.filter(
          (merchant) =>
            stillCoveredIds.has(merchant.merchantId) ||
            !members.some((m) => m.merchantId === merchant.merchantId),
        ),
      );
    } else {
      setSelectedGroupIds([...selectedGroupIds, group.id]);
      const existingIds = new Set(
        pendingSelectedMerchant.map((m) => m.merchantId),
      );
      const toAdd = members.filter((m) => !existingIds.has(m.merchantId));
      setPendingSelectedMerchant([...pendingSelectedMerchant, ...toAdd]);
    }
  };

  const clearSelectedGroups = useCallback(() => setSelectedGroupIds([]), []);

  const openCreateGroupModal = (type = null) => {
    setGroupModalData({
      name: '',
      merchants: [],
      isEdit: false,
      editId: null,
      type,
    });
    openGroupModal();
  };

  const openEditGroupModal = (group, type = null) => {
    setGroupModalData({
      name: group.groupName,
      merchants: group.merchants,
      isEdit: true,
      editId: group.id,
      type,
    });
    openGroupModal();
  };

  const handleGroupModalSubmit = async () => {
    if (!groupModalData.name.trim()) {
      toast({
        title: 'Error',
        description: 'Group name is required',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (groupModalData.merchants.length === 0) {
      toast({
        title: 'Error',
        description: 'Please select at least one merchant',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const groupPayload = {
        name: groupModalData.name,
        merchantIds: groupModalData.merchants.map((m) => m.merchantId),
        ...(groupModalData.type ? { type: groupModalData.type } : {}),
      };

      if (groupModalData.isEdit) {
        await updateGroup(
          groupModalData.editId,
          groupPayload,
          groupModalData.type,
        );
      } else {
        await createGroup(groupPayload, groupModalData.type);
      }

      closeGroupModal();
      setSelectedGroupIds([]);
    } catch (error) {
      // Error handling is done in the API functions
    }
  };

  const handleGroupModalCancel = () => {
    setGroupModalData({
      name: '',
      merchants: [],
      isEdit: false,
      editId: null,
      type: null,
    });
    closeGroupModal();
  };

  const handleGroupMerchantSelection = useCallback((merchant, isSelected) => {
    if (isSelected) {
      setGroupModalData((prev) => ({
        ...prev,
        merchants: [...prev.merchants, merchant],
      }));
    } else {
      setGroupModalData((prev) => ({
        ...prev,
        merchants: prev.merchants.filter(
          (m) => m.merchantId !== merchant.merchantId,
        ),
      }));
    }
  }, []);

  const updateGroupModalData = useCallback((updater) => {
    setGroupModalData(updater);
  }, []);

  const deleteGroup = async (groupId, type = null) => {
    setIsDeleting(true);
    try {
      await requestApi.delete(`${GROUPS}/${groupId}`);
      await fetchGroups(type);
      // Drop the deleted group from the active selection, if it was active
      setSelectedGroupIds((prev) => prev.filter((id) => id !== groupId));
    } catch (error) {
      toast({
        title: 'Error',
        description: error?.message || 'Failed to delete merchant group',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      throw error;
    } finally {
      setIsDeleting(false);
    }
  };

  const handleGroupModalDelete = async () => {
    if (groupModalData.editId) {
      try {
        await deleteGroup(groupModalData.editId, groupModalData.type);
        closeGroupModal();
      } catch (error) {
        // Error handling is done in deleteGroup function
      }
    }
  };

  return {
    merchantGroups,
    selectedGroupIds,
    setSelectedGroupIds,
    isFetchingGroups,
    isCreatingGroup,
    isUpdatingGroup,
    isDeleting,
    groupModalData,
    setGroupModalData: updateGroupModalData,
    isGroupModalOpen,
    openGroupModal,
    closeGroupModal,
    fetchGroups,
    createGroup,
    updateGroup,
    deleteGroup,
    handleGroupSelection,
    clearSelectedGroups,
    openCreateGroupModal,
    openEditGroupModal,
    handleGroupModalSubmit,
    handleGroupModalCancel,
    handleGroupModalDelete,
    handleGroupMerchantSelection,
  };
};

export default useMerchantGroups;
